import {
  GetExamCourseByExamCourseCodeDocument,
  GetPreExamSettingsFromExamCourseSettingsByExamCourseCodeDocument,
  type UpdateExamCourseSettingsMutationOptions,
  useGetExamCourseSettingsByExamCourseCodeLazyQuery,
  useUpdateExamCourseSettingsMutation,
} from '@exam-center/graphql';

import { type PreExamSettingsSectionFormOutputs } from '../../containers/pre-exam-settings-section-form/types';

import { mapExamDurationToExamCourseSettingsInput, mergePreExamSettingsInput } from './utils';

interface UsePreExamSettingsFormSubmissionParams {
  examCourseId: string;
  examCourseCode: string;
  onCompleted?: UpdateExamCourseSettingsMutationOptions['onCompleted'];
  onError?: UpdateExamCourseSettingsMutationOptions['onError'];
}

export default function usePreExamSettingsFormSubmission(
  params: UsePreExamSettingsFormSubmissionParams,
) {
  const { examCourseId, examCourseCode, onCompleted, onError } = params;

  const [getExamCourseSettings, { loading: isGettingExamCourseSettings }] =
    useGetExamCourseSettingsByExamCourseCodeLazyQuery();
  const [updateExamCourseSettings, { loading: isSubmitting }] =
    useUpdateExamCourseSettingsMutation();

  const handleFormSubmit = async (data: PreExamSettingsSectionFormOutputs) => {
    const { data: examCourseSettingsData } = await getExamCourseSettings({
      variables: {
        getExamCourseByExamCourseCodeInput: {
          examCourseCode,
        },
      },
      fetchPolicy: 'network-only',
    });

    const newSettingsInput = {
      examDuration: mapExamDurationToExamCourseSettingsInput(data.examDuration),
    };

    const mergedSettings = mergePreExamSettingsInput(
      examCourseSettingsData?.ecGetExamCourseByExamCourseCode.examCourse.settings,
      newSettingsInput,
    );

    await updateExamCourseSettings({
      variables: {
        updateExamCourseSettingsInput: {
          id: examCourseId,
          settings: mergedSettings,
        },
      },
      onCompleted,
      onError,
      refetchQueries: [
        GetExamCourseByExamCourseCodeDocument,
        GetPreExamSettingsFromExamCourseSettingsByExamCourseCodeDocument,
      ],
      awaitRefetchQueries: true,
    });
  };

  return {
    handleFormSubmit,
    isSubmitting: isSubmitting || isGettingExamCourseSettings,
  };
}
